import React, { useState, useEffect, useRef } from "react";
import {
    ChevronLeft,
    ChevronDown,
    ChevronRight,
    CalendarDays,
    X,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../_css/EscalationManagement.module.css";
import { useNavigate } from "react-router-dom";
import ResponseLoader from "../common/ResponseLoader.js";
import { useDispatch } from "react-redux";
import {
    getAllTickets,
    getAllCompanies,
    notifyTicket,
    getAllTypesCatgeories // Imported API
} from "../../requests.js";
import {
    startLoader,
    stopLoader,
} from "../../_store/reducers/loaderReducer.js";
import { notifyError, notifySuccess } from "../../utils/helper.js";

const DuringFeedback = () => {
    const [data, setData] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [categories, setCategories] = useState([]);

    // Filter states
    const [filters, setFilters] = useState({
        type: "",
        status: "",
        company: "",
    });

    const rowsPerPage = 4;

    // Dropdown toggles
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

    const typeDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);
    const companyDropdownRef = useRef(null);

    // Updated static status list with colors
    const STATUS_OPTIONS = [
        { label: "Open", color: "#3B82F6" },
        { label: "In Progress", color: "#FACC15" },
        { label: "Resolved", color: "#10B981" },
        { label: "Closed", color: "#6B7280" },
        { label: "Pending", color: "#F97316" },
    ];

    // Helper to get color by status
    const getStatusColor = (status) => {
        const found = STATUS_OPTIONS.find(
            (s) => s.label.toLowerCase() === status?.toLowerCase()
        );
        return found ? found.color : "#9CA3AF"; // default gray
    };

    // Build filter payload for API
    const buildFilterPayload = () => {
        const payload = {
            issue_stage: "during feedback",
            page,
            limit: rowsPerPage,
        };

        if (filters.type) payload.type = filters.type;
        if (filters.status) payload.status = filters.status;
        if (filters.company) payload.company = filters.company;
        if (fromDate) payload.from_date = formatDate(fromDate);
        if (toDate) payload.to_date = formatDate(toDate);

        return payload;
    };

    // --- NEW: Fetch Categories from API ---
    const fetchCategories = async () => {
        try {
            const response = await getAllTypesCatgeories({ issue_stage: "during feedback" });
            if (response.success) {
                setCategories(response.data);
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    // Fetch tickets
    const fetchTickets = async () => {
        setLoading(true);
        dispatch(startLoader());
        try {
            const response = await getAllTickets(buildFilterPayload());
            const result = response.data;

            if (result.success) {
                const formatted = result.data.map((item) => ({
                    id: item.id,
                    name: item.user_name,
                    image: item.user_image,
                    category: item.category || "N/A",
                    status: item.status || "Unknown",
                    company: item.company || "N/A",
                    date: item.date ? new Date(item.date).toLocaleDateString() : "N/A",
                    updated_at: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "N/A",
                    push_notification: item.push_status,
                    statusColor: getStatusColor(item.status),
                }));

                // --- COMPANY LOGIC PRESERVED (Based on Data) ---
                const uniqueCompanies = Array.from(
                    new Set(formatted.map(item => item.company))
                ).filter(Boolean);
                setCompanies(uniqueCompanies);

                // --- CATEGORY LOGIC REMOVED (Now handled by fetchCategories) ---

                setData(formatted);
                setTotalPages(result.totalPages || 1);
                setTotalCount(result.totalCount || 0);

                setError(null);
            } else {
                setError(result.message || "Failed to fetch tickets");
                setData([]);
                setTotalPages(1);
                setTotalCount(0);
            }
        } catch (err) {
            console.error("Error fetching tickets:", err);
            setError("Failed to fetch tickets.");
            setData([]);
            setTotalPages(1);
            setTotalCount(0);
        } finally {
            setLoading(false);
            dispatch(stopLoader());
        }
    };

    const formatDate = (date) => {
        if (!date) return null;
        return date.toLocaleDateString("en-CA");
    };

    // Fetch Categories on Mount ---
    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch data on mount and whenever filters or page changes
    useEffect(() => {
        fetchTickets();
    }, [filters.type, filters.status, filters.company, fromDate, toDate, page]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target))
                setShowTypeDropdown(false);
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target))
                setShowStatusDropdown(false);
            if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target))
                setShowCompanyDropdown(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Pagination controls
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Reset filters
    const clearFilters = () => {
        setFilters({ type: "", status: "", company: "" });
        setFromDate(null);
        setToDate(null);
        setSearchTerm("");
        setPage(1);
    };

    // Handle push notification toggle
    const handlePushNotificationToggle = async (ticketId, currentStatus) => {
        const newStatus = !currentStatus;

        // Optimistically update UI
        setData(prevData =>
            prevData.map(item =>
                item.id === ticketId
                    ? { ...item, push_notification: newStatus }
                    : item
            )
        );

        dispatch(startLoader());
        try {
            const response = await notifyTicket(ticketId, newStatus);
            console.log("Response: ", response);

            if (response.success) {
                await fetchTickets();
                notifySuccess(`Notification ${newStatus ? 'sent successfully' : 'disabled successfully'}`);
            } else {
                setData(prevData =>
                    prevData.map(item =>
                        item.id === ticketId
                            ? { ...item, push_notification: currentStatus }
                            : item
                    )
                );
                notifyError(response.message || `Failed to ${newStatus ? 'enable' : 'disable'} notification`);
            }
        } catch (error) {
            console.error('Error updating push notification:', error);
            setData(prevData =>
                prevData.map(item =>
                    item.id === ticketId
                        ? { ...item, push_notification: currentStatus }
                        : item
                )
            );
            notifyError(`Failed to ${newStatus ? 'enable' : 'disable'} notification`);
        } finally {
            dispatch(stopLoader());
        }
    };

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            items.push(
                <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className={styles.pageNumber}
                >
                    01
                </button>
            );
            if (startPage > 2) {
                items.push(<span key="ellipsis1" className={styles.ellipsis}>...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`${styles.pageNumber} ${page === i ? styles.activePage : ""}`}
                >
                    {i.toString().padStart(2, "0")}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<span key="ellipsis2" className={styles.ellipsis}>...</span>);
            }
            items.push(
                <button
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                    className={styles.pageNumber}
                >
                    {totalPages.toString().padStart(2, "0")}
                </button>
            );
        }

        return items;
    };

    const tableHeaders = [
        { label: "T-ID", width: "75px" },
        { label: "Name", width: "150px" },
        { label: "Category", width: "100px" },
        { label: "Status", width: "80px" },
        { label: "Company", width: "100px" },
        { label: "Created At", width: "90px" },
        { label: "Updated At", width: "90px" },
        { label: "Push Notification", width: "120px" },
    ];

    return (
        <div className={styles.loaderContainer}>
            <div className={styles.content}>
                <div className={styles.container}>
                    {/* ---------------- FILTER SECTION ---------------- */}
                    <div className={styles.filterSection}>
                        {/* Type Filter */}
                        <div style={{ position: "relative" }} ref={typeDropdownRef}>
                            <button
                                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                                className={styles.filterButton}
                            >
                                {filters.type || "Type"}{" "}
                                <ChevronDown size={16} color="#6b7280" />
                            </button>
                            {showTypeDropdown && (
                                <div className={styles.dropdown}>
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                    {(categories)
                                        .filter(
                                            (type) =>
                                                type &&
                                                type
                                                    .toLowerCase()
                                                    .includes(searchTerm.toLowerCase())
                                        )
                                        .map((type, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => {
                                                    setFilters((prev) => ({ ...prev, type }));
                                                    setShowTypeDropdown(false);
                                                    setSearchTerm("");
                                                    setPage(1);
                                                }}
                                                className={styles.dropdownItem}
                                            >
                                                {type}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Status Filter */}
                        <div style={{ position: "relative" }} ref={statusDropdownRef}>
                            <button
                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                className={styles.filterButton}
                            >
                                {filters.status || "Status"}{" "}
                                <ChevronDown size={16} color="#6b7280" />
                            </button>
                            {showStatusDropdown && (
                                <div className={styles.dropdown}>
                                    {STATUS_OPTIONS.map((status, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    status: status.label,
                                                }));
                                                setShowStatusDropdown(false);
                                                setPage(1);
                                            }}
                                            className={styles.dropdownItem}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    backgroundColor: status.color,
                                                }}
                                            ></span>
                                            {status.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Company Filter */}
                        <div style={{ position: "relative" }} ref={companyDropdownRef}>
                            <button
                                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                                className={styles.filterButton}
                            >
                                {filters.company || "Company"}{" "}
                                <ChevronDown size={16} color="#6b7280" />
                            </button>
                            {showCompanyDropdown && (
                                <div className={styles.dropdown}>
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                    {companies
                                        .filter(
                                            (company) =>
                                                company &&
                                                company
                                                    .toLowerCase()
                                                    .includes(searchTerm.toLowerCase())
                                        )
                                        .map((company, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => {
                                                    setFilters((prev) => ({ ...prev, company }));
                                                    setShowCompanyDropdown(false);
                                                    setSearchTerm("");
                                                    setPage(1);
                                                }}
                                                className={styles.dropdownItem}
                                            >
                                                {company}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Date Filters */}
                        <div className={styles.datePickerContainer}>
                            <CalendarDays size={16} color="#6B7280" />
                            <DatePicker
                                selected={fromDate}
                                onChange={(date) => {
                                    setFromDate(date);
                                    setPage(1);
                                }}
                                placeholderText="From Date"
                                popperPlacement="bottom-start"
                                className={styles.customDatepicker}
                            />
                        </div>
                        <div className={styles.datePickerContainer}>
                            <CalendarDays size={16} color="#6B7280" />
                            <DatePicker
                                selected={toDate}
                                onChange={(date) => {
                                    setToDate(date);
                                    setPage(1);
                                }}
                                placeholderText="To Date"
                                popperPlacement="bottom-start"
                                className={styles.customDatepicker}
                            />
                        </div>

                        <button
                            onClick={clearFilters}
                            className={styles.filterButton}
                            style={{ marginLeft: "auto" }} // Pushes button to the end if container is flex
                            title="Clear all filters"
                        >
                            Clear
                            <X size={16} color="#6b7280" />
                        </button>

                    </div>

                    {/* ---------------- TABLE SECTION ---------------- */}
                    <div className={styles.tableSection}>
                        <table className={styles.table}>
                            <thead style={{ color: "#667085" }}>
                                <tr>
                                    {tableHeaders.map(({ label, width }, i) => (
                                        <th
                                            key={i}
                                            className={styles.tableHeader}
                                            style={{ width }}
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={tableHeaders.length}
                                            style={{ textAlign: "center" }}
                                        >
                                            <ResponseLoader />
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={tableHeaders.length}
                                            className={styles.noDataCell}
                                        >
                                            {error ? error : "No tickets found"}
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((item, idx) => (
                                        <tr key={idx}>
                                            <td
                                                className={styles.leftBorderCell}
                                                onClick={() =>
                                                    navigate("/escalation/ticketdetails", {
                                                        state: { ticketId: item.id, activeTab: "During Feedback" },
                                                    })
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                {item.id}
                                            </td>
                                            <td className={styles.cell}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <img
                                                        src={
                                                            item.image ||
                                                            `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name)}&background=23b4c3&color=fff`
                                                        }
                                                        alt={item.name}
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            objectFit: "cover",
                                                        }}
                                                        className={styles.table_avatar}
                                                    />
                                                    <span>{item.name}</span>
                                                </div>
                                            </td>
                                            <td className={styles.cell}>{item.category}</td>
                                            <td className={styles.cell}>
                                                <span className={styles.statusContainer}>
                                                    <span
                                                        className={styles.statusIndicator}
                                                        style={{
                                                            backgroundColor: item.statusColor,
                                                        }}
                                                    ></span>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className={styles.cell}>{item.company}</td>
                                            <td className={styles.cell}>{item.date}</td>
                                            <td className={styles.cell}>{item.updated_at}</td>
                                            <td className={styles.rightBorderCell}>
                                                <button
                                                    className={
                                                        item.push_notification
                                                            ? styles.pushEnabledButton
                                                            : styles.pushDisabledButton
                                                    }
                                                    style={{
                                                        cursor: item.push_notification ? "not-allowed" : "pointer"
                                                    }}
                                                    disabled={item.push_notification === true}
                                                    onClick={() =>
                                                        handlePushNotificationToggle(item.id, item.push_notification)
                                                    }
                                                >
                                                    {item.push_notification ? "Sent" : "Send"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ---------------- PAGINATION ---------------- */}
                    {!loading && totalPages > 0 && (
                        <div className={styles.paginationSection}>
                            <div className={styles.paginationContainer}>
                                <button
                                    disabled={page === 1}
                                    onClick={() => handlePageChange(page - 1)}
                                    className={styles.paginationButton}
                                >
                                    <ChevronLeft size={16} />
                                    Previous
                                </button>

                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                    {renderPaginationItems()}
                                </div>

                                <button
                                    disabled={page === totalPages}
                                    onClick={() => handlePageChange(page + 1)}
                                    className={styles.paginationButton}
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DuringFeedback;